import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  ListSubheader,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    },
    '&.Mui-focused': {
      boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)',
    }
  }
}));

const FlightSearch = ({ onSelect, origin, destination, dates, passengers }) => {
  const [loading, setLoading] = useState(false);
  const [flights, setFlights] = useState([]);
  const [error, setError] = useState(null);
  const [originSearch, setOriginSearch] = useState('');
  const [destinationSearch, setDestinationSearch] = useState('');
  const [originAirports, setOriginAirports] = useState([]);
  const [destinationAirports, setDestinationAirports] = useState([]);
  const [selectedOriginAirport, setSelectedOriginAirport] = useState(null);
  const [selectedDestinationAirport, setSelectedDestinationAirport] = useState(null);

  // Search for airports
  const searchAirports = async (query, isOrigin = true) => {
    if (!query || query.length < 2) {
      if (isOrigin) {
        setOriginAirports([]);
      } else {
        setDestinationAirports([]);
      }
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_API_URL}/search-airport`, {
        params: { query }
      });
      
      if (response.data.status && response.data.data) {
        if (isOrigin) {
          setOriginAirports(response.data.data);
        } else {
          setDestinationAirports(response.data.data);
        }
      }
    } catch (err) {
      console.error('Airport search error:', err);
      setError('Failed to find airports. Please try again.');
    }
  };

  // Search for flights
  const searchFlights = async () => {
    if (!selectedOriginAirport || !selectedDestinationAirport) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${BACKEND_API_URL}/search-flights`, {
        params: {
          originSkyId: selectedOriginAirport.skyId,
          destinationSkyId: selectedDestinationAirport.skyId,
          originEntityId: selectedOriginAirport.entityId,
          destinationEntityId: selectedDestinationAirport.entityId,
          date: dates.arrival,
          returnDate: dates.departure,
          adults: passengers.adults,
          childrens: passengers.children || 0,
          cabinClass: 'economy'
        }
      });

      if (response.data.status && response.data.data.itineraries) {
        setFlights(response.data.data.itineraries);
      } else {
        setError('No flights found for these dates.');
      }
    } catch (err) {
      console.error('Flight search error:', err);
      setError('Failed to search flights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Debounce the search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (originSearch) {
        searchAirports(originSearch, true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [originSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (destinationSearch) {
        searchAirports(destinationSearch, false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [destinationSearch]);

  // Search for flights when both airports are selected
  useEffect(() => {
    if (selectedOriginAirport && selectedDestinationAirport) {
      searchFlights();
    }
  }, [selectedOriginAirport, selectedDestinationAirport]);

  // Initialize searches with provided origin/destination
  useEffect(() => {
    if (origin && !originSearch) {
      setOriginSearch(origin);
    }
    if (destination && !destinationSearch) {
      setDestinationSearch(destination);
    }
  }, [origin, destination]);

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <StyledFormControl fullWidth>
            <InputLabel>Departure Airport</InputLabel>
            <Select
              value={selectedOriginAirport ? selectedOriginAirport.skyId : ''}
              input={<OutlinedInput label="Departure Airport" />}
              onChange={(e) => {
                const airport = originAirports.find(a => a.skyId === e.target.value);
                setSelectedOriginAirport(airport);
              }}
              onClose={() => setOriginAirports([])}
              renderValue={() => selectedOriginAirport ? 
                `${selectedOriginAirport.name} (${selectedOriginAirport.skyId}) - ${selectedOriginAirport.city}` : ''
              }
            >
              <ListSubheader>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Type to search airports..."
                  fullWidth
                  value={originSearch}
                  onChange={(e) => setOriginSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </ListSubheader>
              {originAirports.map((airport) => (
                <MenuItem key={airport.skyId} value={airport.skyId}>
                  {airport.name} ({airport.skyId}) - {airport.city}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledFormControl fullWidth>
            <InputLabel>Arrival Airport</InputLabel>
            <Select
              value={selectedDestinationAirport ? selectedDestinationAirport.skyId : ''}
              input={<OutlinedInput label="Arrival Airport" />}
              onChange={(e) => {
                const airport = destinationAirports.find(a => a.skyId === e.target.value);
                setSelectedDestinationAirport(airport);
              }}
              onClose={() => setDestinationAirports([])}
              renderValue={() => selectedDestinationAirport ? 
                `${selectedDestinationAirport.name} (${selectedDestinationAirport.skyId}) - ${selectedDestinationAirport.city}` : ''
              }
            >
              <ListSubheader>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Type to search airports..."
                  fullWidth
                  value={destinationSearch}
                  onChange={(e) => setDestinationSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </ListSubheader>
              {destinationAirports.map((airport) => (
                <MenuItem key={airport.skyId} value={airport.skyId}>
                  {airport.name} ({airport.skyId}) - {airport.city}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Grid>
      </Grid>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Grid container spacing={2}>
          {flights.map((flight) => (
            <Grid item xs={12} key={flight.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
                onClick={() => onSelect(flight)}
              >
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography variant="h6">
                        {flight.legs[0].origin.name} ({flight.legs[0].origin.displayCode}) → 
                        {flight.legs[0].destination.name} ({flight.legs[0].destination.displayCode})
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {flight.legs[0].carriers.marketing[0].name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body1">
                        {new Date(flight.legs[0].departure).toLocaleTimeString()} - 
                        {new Date(flight.legs[0].arrival).toLocaleTimeString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Duration: {flight.legs[0].durationInMinutes} mins
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="h6" color="primary">
                        {flight.price.formatted}
                      </Typography>
                      {flight.legs[0].stopCount > 0 && (
                        <Chip
                          size="small"
                          label={`${flight.legs[0].stopCount} stop${flight.legs[0].stopCount > 1 ? 's' : ''}`}
                          color="default"
                        />
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default FlightSearch;
